/** The two texture containers the environment records name, decoded to RGBA.
 *
 * `CResourceManager` hands a `.tga` or a `.dds` to `CGraphicImage`, and the
 * two shapes that reach it here are a 32-bit uncompressed Targa (the cloud
 * sheets) and a DDS holding either DXT1 blocks (`flare1`..`flare6`) or an
 * uncompressed A8R8G8B8 surface (`sunflare.dds`). A container this does not
 * know is null, never a throw: the original draws nothing for a texture it
 * could not load either.
 */

/** A decoded image, ready for `texImage2D`. */
export interface RgbaImage {
  readonly width: number;
  readonly height: number;
  readonly pixels: Uint8Array;
}

export function decodeTga(bytes: ArrayBuffer): RgbaImage | null {
  const data = new Uint8Array(bytes);
  if (data.length < 18) return null;
  const idLength = data[0];
  const colourMapType = data[1];
  const imageType = data[2];
  const width = data[12] | (data[13] << 8);
  const height = data[14] | (data[15] << 8);
  const depth = data[16];
  const descriptor = data[17];
  if (colourMapType !== 0 || imageType !== 2 || depth !== 32) return null;
  const start = 18 + idLength;
  const count = width * height;
  if (width <= 0 || height <= 0 || data.length < start + count * 4) return null;

  const pixels = new Uint8Array(count * 4);
  // Bit 5 of the image descriptor: set means the first row in the file is the
  // top one. Every one of these four files has it clear, so the rows arrive
  // bottom-up and are flipped here.
  const topDown = (descriptor & 0x20) !== 0;
  for (let row = 0; row < height; row += 1) {
    const source = start + row * width * 4;
    const target = (topDown ? row : height - 1 - row) * width * 4;
    for (let column = 0; column < width; column += 1) {
      const from = source + column * 4;
      const to = target + column * 4;
      pixels[to + 0] = data[from + 2];
      pixels[to + 1] = data[from + 1];
      pixels[to + 2] = data[from + 0];
      pixels[to + 3] = data[from + 3];
    }
  }
  return { width, height, pixels };
}

const DDS_MAGIC = 0x20534444; // "DDS " little-endian
const DDPF_ALPHAPIXELS = 0x1;
const DDPF_FOURCC = 0x4;
const DDPF_RGB = 0x40;

/** The top surface of a DDS, as RGBA. Mip levels below it are ignored: every
 *  quad this draws is screen-sized and sampled at one level. */
export function decodeDds(bytes: ArrayBuffer): RgbaImage | null {
  const data = new Uint8Array(bytes);
  if (data.length < 128) return null;
  const view = new DataView(bytes);
  if (view.getUint32(0, true) !== DDS_MAGIC) return null;
  if (view.getUint32(4, true) !== 124) return null;
  const height = view.getUint32(12, true);
  const width = view.getUint32(16, true);
  if (width <= 0 || height <= 0 || width > 8192 || height > 8192) return null;
  const pixelFlags = view.getUint32(80, true);
  const fourCC = view.getUint32(84, true);
  const bitCount = view.getUint32(88, true);

  if ((pixelFlags & DDPF_FOURCC) !== 0) {
    // "DXT1" only: the six flare sprites are the only DXT this reads, and a
    // block shape guessed wrong is a texture of noise rather than an error.
    if (fourCC !== 0x31545844) return null;
    return decodeDxt1(data.subarray(128), width, height);
  }
  if ((pixelFlags & DDPF_RGB) === 0 || bitCount !== 32) return null;
  // A8R8G8B8, the one uncompressed layout in this pack (`sunflare.dds`).
  if (view.getUint32(92, true) !== 0x00ff0000) return null;
  if (view.getUint32(96, true) !== 0x0000ff00) return null;
  if (view.getUint32(100, true) !== 0x000000ff) return null;
  const opaque = (pixelFlags & DDPF_ALPHAPIXELS) === 0;
  const count = width * height;
  if (data.length < 128 + count * 4) return null;
  const pixels = new Uint8Array(count * 4);
  for (let at = 0; at < count; at += 1) {
    const from = 128 + at * 4;
    pixels[at * 4 + 0] = data[from + 2];
    pixels[at * 4 + 1] = data[from + 1];
    pixels[at * 4 + 2] = data[from + 0];
    pixels[at * 4 + 3] = opaque ? 255 : data[from + 3];
  }
  return { width, height, pixels };
}

/** BC1: one 8-byte block per 4x4 texels -- two RGB565 endpoints and sixteen
 *  two-bit selectors. `c0 <= c1` is the one-bit-alpha mode, where selector 3 is
 *  transparent black and the third colour is the midpoint rather than a third. */
function decodeDxt1(blocks: Uint8Array, width: number, height: number): RgbaImage | null {
  const across = Math.ceil(width / 4);
  const down = Math.ceil(height / 4);
  if (blocks.length < across * down * 8) return null;
  const pixels = new Uint8Array(width * height * 4);
  const red = new Uint8Array(4);
  const green = new Uint8Array(4);
  const blue = new Uint8Array(4);
  const alpha = new Uint8Array(4);
  for (let blockY = 0; blockY < down; blockY += 1) {
    for (let blockX = 0; blockX < across; blockX += 1) {
      const at = (blockY * across + blockX) * 8;
      const c0 = blocks[at] | (blocks[at + 1] << 8);
      const c1 = blocks[at + 2] | (blocks[at + 3] << 8);
      // 5:6:5 widened to 8 bits by replicating the high bits, which is what
      // every hardware decoder does and what keeps white at 255.
      red[0] = ((c0 >> 11) & 31) * 255 / 31;
      green[0] = ((c0 >> 5) & 63) * 255 / 63;
      blue[0] = (c0 & 31) * 255 / 31;
      red[1] = ((c1 >> 11) & 31) * 255 / 31;
      green[1] = ((c1 >> 5) & 63) * 255 / 63;
      blue[1] = (c1 & 31) * 255 / 31;
      alpha[0] = alpha[1] = alpha[2] = 255;
      if (c0 > c1) {
        red[2] = (2 * red[0] + red[1]) / 3;
        green[2] = (2 * green[0] + green[1]) / 3;
        blue[2] = (2 * blue[0] + blue[1]) / 3;
        red[3] = (red[0] + 2 * red[1]) / 3;
        green[3] = (green[0] + 2 * green[1]) / 3;
        blue[3] = (blue[0] + 2 * blue[1]) / 3;
        alpha[3] = 255;
      } else {
        red[2] = (red[0] + red[1]) / 2;
        green[2] = (green[0] + green[1]) / 2;
        blue[2] = (blue[0] + blue[1]) / 2;
        red[3] = green[3] = blue[3] = 0;
        alpha[3] = 0;
      }
      const selectors =
        blocks[at + 4] | (blocks[at + 5] << 8) | (blocks[at + 6] << 16) |
        (blocks[at + 7] * 0x1000000);
      for (let row = 0; row < 4; row += 1) {
        const y = blockY * 4 + row;
        if (y >= height) break;
        for (let column = 0; column < 4; column += 1) {
          const x = blockX * 4 + column;
          if (x >= width) break;
          const pick = (selectors >>> ((row * 4 + column) * 2)) & 3;
          const to = (y * width + x) * 4;
          pixels[to + 0] = red[pick];
          pixels[to + 1] = green[pick];
          pixels[to + 2] = blue[pick];
          pixels[to + 3] = alpha[pick];
        }
      }
    }
  }
  return { width, height, pixels };
}

/** Whichever of the two the bytes turn out to be, chosen by content rather
 *  than by the name the record spells: `guild_village.msenv` names a `.dds`
 *  and `milgyo.msenv` a `.TGA` for the same field. */
export function decodeImage(bytes: ArrayBuffer): RgbaImage | null {
  return decodeDds(bytes) ?? decodeTga(bytes);
}
